import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateChecklistModal from '../admin-dashboard/components/CreateChecklistModal';
import ChecklistManagementTable from '../admin-dashboard/components/ChecklistManagementTable';
import ChecklistDeleteConfirmModal from './components/ChecklistDeleteConfirmModal';
import { checklistService } from '../../services/checklistService';
import { siteService } from '../../services/siteService';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

const ChecklistManagement = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [checklists, setChecklists] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);

  // NSherjFh PR 8: tab between active and soft-deleted checklist
  // templates. Admin-only. RLS would permit site_managers + workers
  // to soft-delete via admins_manage_checklists FOR ALL, but the UI
  // exposes Delete + Restore + the toggle to admin only. Same RLS-
  // permissive-vs-UI-gated pattern as PRs 4-6-7.
  const isAdmin = userProfile?.role === 'admin';
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingChecklist, setDeletingChecklist] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [viewDeleted]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sitesResult, checklistsResult] = await Promise.all([
        siteService?.getAllSites(),
        checklistService?.getAllChecklistTemplates({ includeDeleted: viewDeleted })
      ]);

      if (sitesResult?.data) {
        setSites(sitesResult?.data);
      }

      // NSherjFh PR 8: deleted tab shows ONLY soft-deleted templates.
      // includeDeleted: true returns BOTH active and deleted; narrow
      // to the deleted ones for display.
      const rows = checklistsResult?.data || [];
      setChecklists(viewDeleted ? rows?.filter((c) => c?.deleted_at) : rows);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChecklist = async (formData) => {
    try {
      if (formData?.siteId) {
        const { data: pickedSite } = await supabase
          ?.from('sites')
          ?.select('status')
          ?.eq('id', formData?.siteId)
          ?.maybeSingle();
        if (isSiteReadOnlyStatus(pickedSite?.status)) {
          toast?.error('This site is completed — please reactivate to continue.');
          return;
        }
      }
      const templateResult = await checklistService?.createChecklistTemplate({
        siteId: formData?.siteId,
        companyId: userProfile?.company_id,
        checklistName: formData?.checklistName,
        description: formData?.description
      });

      if (templateResult?.data) {
        for (let i = 0; i < formData?.items?.length; i++) {
          const item = formData?.items?.[i];
          await checklistService?.createChecklistItem({
            checklistId: templateResult?.data?.id,
            category: 'General',
            itemDescription: item?.itemDescription,
            isMandatory: item?.isMandatory,
            displayOrder: i
          });
        }
        loadData();
      }
    } catch (err) {
      console.error('Error creating checklist:', err);
    }
  };

  const handleEditChecklist = (checklist) => {
    setSelectedChecklist(checklist);
    // Future: Open edit modal
  };

  // NSherjFh PR 8 — soft-delete handlers. Replaces the prior
  // window.confirm() + hard-DELETE flow with modal + idempotent
  // soft-delete UPDATE. ChecklistManagementTable now passes the
  // full checklist object to onDelete / onRestore (renamed from
  // onDelete(checklist.id) so the modal can show the name).
  const handleDeleteClick = (checklist) => {
    setDeletingChecklist(checklist);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingChecklist?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingChecklist;
    try {
      const result = await checklistService?.deleteChecklistTemplate(target?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Delete failed');
      setChecklists((prev) => prev?.filter((c) => c?.id !== target?.id));
      toast?.success('Checklist template moved to Deleted');
    } catch (err) {
      console.error('[ChecklistManagement] delete failed:', err);
      toast?.error(err?.message || 'Failed to delete checklist template');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingChecklist(null);
    }
  };

  const handleRestore = async (checklist) => {
    try {
      const result = await checklistService?.restoreChecklistTemplate(checklist?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Restore failed');
      setChecklists((prev) => prev?.filter((c) => c?.id !== checklist?.id));
      toast?.success('Checklist template restored');
    } catch (err) {
      console.error('[ChecklistManagement] restore failed:', err);
      toast?.error(err?.message || 'Failed to restore checklist template');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            iconName="ArrowLeft"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            Back
          </Button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {viewDeleted ? 'Deleted checklist templates' : 'Checklist Management'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {viewDeleted
                  ? 'Templates removed from active views. Restore to bring them back.'
                  : 'Create and manage daily checklist templates for your sites'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* NSherjFh PR 8: admin-only View Deleted toggle. */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  onClick={() => setViewDeleted((v) => !v)}
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
              {!viewDeleted && (
                <Button
                  iconName="Plus"
                  onClick={() => setShowCreateModal(true)}
                  disabled={subscriptionReadOnly}
                >
                  Create Checklist Template
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 border border-border">
          <div className="p-4 md:p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                <Icon name="ClipboardList" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Checklist Templates</h2>
                <p className="text-sm text-muted-foreground">Manage templates that workers can complete daily</p>
              </div>
            </div>
          </div>

          <ChecklistManagementTable
            checklists={checklists}
            sites={sites}
            onEdit={handleEditChecklist}
            onDelete={!viewDeleted && isAdmin ? handleDeleteClick : null}
            onRestore={viewDeleted && isAdmin ? handleRestore : null}
            loading={loading}
          />
        </div>

        <div className="mt-6 bg-accent/10 border border-accent/20 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <div>
              <p className="text-sm text-foreground font-medium mb-1">Admin-Only Access</p>
              <p className="text-sm text-muted-foreground">
                Only administrators can create, edit, and delete checklist templates. Workers can view and complete checklists assigned to their sites.
              </p>
            </div>
          </div>
        </div>
      </main>

      <CreateChecklistModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateChecklist}
        sites={sites}
      />

      <ChecklistDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeletingChecklist(null); }}
        onConfirm={handleConfirmDelete}
        checklistName={deletingChecklist?.checklist_name || ''}
      />
    </div>
  );
};

export default ChecklistManagement;